import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { GetCartById } from "../../hooks/fetch-hooks/cart-details/get-cart-by-id";
import Loader from "../../components/Loader";
import { formatDateTime } from "../../utils/DatetimeFormat-table";
import { request } from "../../config/helpers/axios-instance";
import { toast } from "react-toastify";
import ReminderTimings from "./components/ReminderTimings";
// Grounding & Pricing Detail is hidden for now (audit info to be handled separately)
// import MedicineRow from "../../components/cart-grounding/MedicineRow";
// import { MdInsights } from "react-icons/md";

const STATUS_COLORS = {
    CART_ACTIVE: "bg-green-100 text-green-700",
    CART_LOCKED: "bg-yellow-100 text-yellow-700",
    CART_ABANDONED: "bg-red-100 text-red-700",
    CART_PAID: "bg-blue-100 text-blue-700",
};

const PRICING_STATUS_COLORS = {
    priced: "text-green-600",
    unpriced: "text-red-500",
    partial: "text-yellow-600",
};

export default function EditCart() {
    const { cartId } = useParams();
    const navigate = useNavigate();
    const { data, isFetched, refetch } = GetCartById(cartId);
    const [refundLoading, setRefundLoading] = useState(false);
    const [rxRequestLoading, setRxRequestLoading] = useState(false);
    const [linking, setLinking] = useState(false);
    const [quotationInput, setQuotationInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cartQuotationSyncLoading, setCartQuotationSyncLoading] = useState(false);

    const cart = data?.data?.data;

    const { data: paymentData, refetch: refetchPayments } = useQuery(
        ["cart-payments", cart?.quotationId ?? cart?.cartId],
        () => cart?.quotationId
            ? request({ url: `payment/admin/quotation/${cart.quotationId}`, method: "GET" })
            : request({ url: `payment/admin/cart/${cart.cartId}`, method: "GET" }),
        { enabled: !!(cart?.quotationId || cart?.cartId), refetchOnWindowFocus: false }
    );
    const paymentStatus = paymentData?.data?.data?.payments ?? paymentData?.data?.data;

    const { data: prescriptionData } = useQuery(
        ["cart-prescription", cart?.prescriptionId],
        () => request({ url: `prescription/admin?id=${cart.prescriptionId}`, method: "GET" }),
        { enabled: !!cart?.prescriptionId, refetchOnWindowFocus: false }
    );
    const rxLinks = prescriptionData?.data?.data?.rows?.[0]?.links ?? [];

    // Grounding & Pricing Detail is hidden for now
    // const { data: pricingDetailData } = useQuery(
    //     ["cart-pricing-detail", cartId],
    //     () => request({ url: `ops/pricing/cart/${cartId}`, method: "GET" }),
    //     { enabled: !!cartId, refetchOnWindowFocus: false }
    // );
    // const pricingLines = pricingDetailData?.data?.data?.lines ?? [];

    const handleSendRxRequest = async () => {
        if (rxRequestLoading || !cart?.cartId) return;
        setRxRequestLoading(true);
        try {
            const res = await request({ url: `myRx/placeConsultationRequest`, method: "POST", data: { cartId: cart.cartId } });
            if (res?.data?.success) {
                toast.success("Rx request sent successfully");
            } else {
                // The API envelope carries `error`, not `message` (utils.sendResponse).
                toast.error(res?.data?.error || "Failed to send Rx request");
            }
        } catch {
            toast.error("Failed to send Rx request");
        }
        setRxRequestLoading(false);
    };

    const handleRefund = async () => {
        if (refundLoading || (!cart?.quotationId && !cart?.cartId)) return;
        setRefundLoading(true);
        const refundBody = cart?.quotationId
            ? { quotationId: cart.quotationId }
            : { cartId: cart.cartId };
        try {
            const res = await request({ url: `payment/issueRefund`, method: "POST", data: { ...refundBody, amount: paymentStatus?.[0]?.amount } });
            if (res?.data?.success) {
                toast.success("Refund initiated successfully");
                refetchPayments();
            } else {
                toast.error(res?.data?.message || "Refund failed");
            }
        } catch {
            toast.error("Refund failed");
        }
        setRefundLoading(false);
    };

    const handleLinkQuotation = async () => {
        const parsed = parseInt(quotationInput, 10);
        if (!quotationInput || isNaN(parsed) || String(parsed) !== quotationInput.trim()) {
            toast.error("Please enter a valid numeric Quotation ID");
            return;
        }
        setIsSubmitting(true);
        try {
            const res = await request({
                url: `cart/${cart.cartId}/link-quotation/${parsed}`,
                method: "PUT",
            });
            if (res?.data?.success) {
                toast.success("Quotation linked successfully");
                setLinking(false);
                setQuotationInput('');
                refetch();
            } else {
                toast.error(res?.data?.message || "Failed to link quotation");
            }
        } catch {
            toast.error("Failed to link quotation");
        }
        setIsSubmitting(false);
    };

    const handleCartQuotationSync = async () => {
        if (cartQuotationSyncLoading || !cart?.cartId) return;
        setCartQuotationSyncLoading(true);
        try {
            const res = await request({ url: `cart/${cart.cartId}/cartQuotationSync`, method: "PUT" });
            if (res?.data?.success) {
                toast.success("Cart quotation synced successfully");
                refetch();
            } else {
                toast.error(res?.data?.message || "Failed to sync cart quotation");
            }
        } catch {
            toast.error("Failed to sync cart quotation");
        }
        setCartQuotationSyncLoading(false);
    };

    if (!isFetched) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader size="large" text="Loading cart details..." />
            </div>
        );
    }

    if (!cart) {
        return (
            <div className="flex items-center justify-center h-full text-gray-500 font-sans">
                Cart not found.
            </div>
        );
    }

    return (
        <div className="py-3 px-5 flex flex-col gap-4 relative h-full overflow-y-scroll">

            {/* Header */}
            <div className="bg-white rounded-2xl shadow-soft-xl p-5 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-semibold font-sans text-gray-800">
                        Cart #{cart.cartId}
                    </h1>
                    <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-md text-sm font-semibold ${STATUS_COLORS[cart.status] || "bg-gray-100 text-gray-700"}`}>
                            {cart.status}
                        </span>
                        {rxLinks.length > 0 && rxLinks.map((link, i) => (
                            <a
                                key={i}
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl font-sans text-sm font-semibold hover:bg-blue-100"
                            >
                                {rxLinks.length > 1 ? `Open Rx ${i + 1}` : "Open Rx"}
                            </a>
                        ))}
                        <button
                            onClick={handleSendRxRequest}
                            disabled={rxRequestLoading}
                            className={`px-4 py-2 bg-[#902b8b] text-white-50 rounded-xl font-sans text-sm font-semibold hover:bg-[#7a2478] transition-colors ${rxRequestLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            {rxRequestLoading ? "Sending…" : "Send Rx Request"}
                        </button>
                        {cart.quotationId ? (
                            <>
                                <button
                                    onClick={() => navigate(`/quotation/edit/${cart.quotationId}`)}
                                    className="px-4 py-2 bg-[#069079] text-white-50 rounded-xl font-sans text-sm font-semibold hover:bg-[#057a65]"
                                >
                                    Go To Quotation
                                </button>
                                <button
                                    onClick={handleCartQuotationSync}
                                    disabled={cartQuotationSyncLoading}
                                    className={`px-4 py-2 bg-[#069079] text-white-50 rounded-xl font-sans text-sm font-semibold hover:bg-[#057a65] transition-colors ${cartQuotationSyncLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    {cartQuotationSyncLoading ? "Syncing…" : "Cart Quotation Sync"}
                                </button>
                            </>
                        ) : linking ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoFocus
                                    value={quotationInput}
                                    onChange={e => setQuotationInput(e.target.value)}
                                    placeholder="Quotation ID"
                                    className="w-36 px-3 py-2 border border-gray-300 rounded-xl font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#069079]"
                                    onKeyDown={e => { if (e.key === 'Enter') handleLinkQuotation(); if (e.key === 'Escape') { setLinking(false); setQuotationInput(''); } }}
                                />
                                <button
                                    onClick={handleLinkQuotation}
                                    disabled={isSubmitting}
                                    className={`px-4 py-2 bg-[#069079] text-white-50 rounded-xl font-sans text-sm font-semibold hover:bg-[#057a65] transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    {isSubmitting ? 'Linking…' : 'Link'}
                                </button>
                                <button
                                    onClick={() => { setLinking(false); setQuotationInput(''); }}
                                    className="px-3 py-2 rounded-xl border border-gray-300 text-gray-500 font-sans text-sm hover:bg-gray-100 transition-colors cursor-pointer"
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setLinking(true)}
                                className="px-4 py-2 bg-[#069079] text-white-50 rounded-xl font-sans text-sm font-semibold hover:bg-[#057a65]"
                            >
                                Link Quotation
                            </button>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2 text-sm font-sans text-gray-600">
                    <div><span className="font-medium text-gray-700">User:</span> {cart.user?.name ? `${cart.user.name} (${cart.userId})` : cart.userId}</div>
                    <div><span className="font-medium text-gray-700">Contact:</span> {cart.user?.contactNo ?? "—"}</div>
                    <div><span className="font-medium text-gray-700">Tenant ID:</span> {cart.tenantId ?? "—"}</div>
                    <div><span className="font-medium text-gray-700">Prescription ID:</span> {cart.prescriptionId ?? "—"}</div>
                    <div><span className="font-medium text-gray-700">Quotation ID:</span> {cart.quotationId ?? "—"}</div>
                    <div><span className="font-medium text-gray-700">Created:</span> {formatDateTime(cart.createdAt)}</div>
                    <div><span className="font-medium text-gray-700">Updated:</span> {formatDateTime(cart.updatedAt)}</div>
                    <div>
                        <span className="font-medium text-gray-700">All Priced:</span>{" "}
                        <span className={cart.allPriced ? "text-green-600" : "text-red-500"}>
                            {cart.allPriced ? "Yes" : "No"}
                        </span>
                    </div>
                </div>
            </div>

            <ReminderTimings reminderTimings={cart.reminderTimings} />

            {/* Payment Status */}
            {paymentStatus && (() => {
                const payments = Array.isArray(paymentStatus) ? paymentStatus : (paymentStatus.payments ?? []);
                const hasSuccessPayment = payments.some(p => p.status === "SUCCESS");
                const amountPaid = payments.filter(p => p.status === "SUCCESS").reduce((sum, p) => sum + parseFloat(p.amount), 0);
                const fullyPaid = paymentStatus.fullyPaid ?? hasSuccessPayment;
                return (
                    <div className="bg-white rounded-2xl shadow-soft-xl p-5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-6">
                            <div className="flex flex-col gap-0.5">
                                <span className="text-xs text-gray-400 font-medium font-sans uppercase tracking-wide">Amount Paid</span>
                                <span className="text-lg font-semibold font-sans text-gray-800">
                                    {amountPaid.toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 })}
                                </span>
                            </div>
                            {paymentStatus.remainingPayable > 0 && (
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs text-gray-400 font-medium font-sans uppercase tracking-wide">Remaining</span>
                                    <span className="text-lg font-semibold font-sans text-red-500">
                                        {parseFloat(paymentStatus.remainingPayable).toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                            )}
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold font-sans ${fullyPaid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                {fullyPaid ? "Fully Paid" : "Unpaid / Partial"}
                            </span>
                        </div>
                        {hasSuccessPayment && (
                            <button
                                onClick={handleRefund}
                                disabled={refundLoading}
                                className={`px-4 py-2 rounded-xl border border-red-300 text-red-500 font-semibold font-sans text-sm hover:bg-red-50 transition-colors whitespace-nowrap ${refundLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                            >
                                {refundLoading ? "Processing…" : "Issue Refund"}
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Items Table */}
            <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                <h2 className="text-lg font-semibold font-sans text-gray-700 mb-4">
                    Items ({cart.items?.length || 0})
                </h2>
                <div className="overflow-x-auto">
                    <table className="table-auto w-full font-sans text-sm">
                        <thead>
                            <tr className="text-left border-b-2 border-gray-100 h-12 text-gray-600">
                                <th className="pl-3 font-semibold">Medicine</th>
                                <th className="pl-3 font-semibold">Qty</th>
                                <th className="pl-3 font-semibold">Unit Price (₹)</th>
                                <th className="pl-3 font-semibold">Unit MRP (₹)</th>
                                <th className="pl-3 font-semibold">Line Total (₹)</th>
                                <th className="pl-3 font-semibold">Line MRP (₹)</th>
                                <th className="pl-3 font-semibold">Pricing</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cart.items?.map((item) => (
                                <tr
                                    key={item.cartItemId}
                                    className="border-t border-gray-100 h-12 hover:bg-gray-50"
                                >
                                    <td className="pl-3 py-3 font-medium text-gray-800">{item.itemName}</td>
                                    <td className="pl-3 py-3 text-gray-600">{item.quantity}</td>
                                    <td className="pl-3 py-3 text-gray-600">{item.unitPrice ?? "—"}</td>
                                    <td className="pl-3 py-3 text-gray-600">{item.unitMrp ?? "—"}</td>
                                    <td className="pl-3 py-3 text-gray-600">{item.lineTotal ?? "—"}</td>
                                    <td className="pl-3 py-3 text-gray-600">{item.lineMrpTotal ?? "—"}</td>
                                    <td className="pl-3 py-3">
                                        <span className={`text-xs font-semibold ${PRICING_STATUS_COLORS[item.pricingStatus] || "text-gray-500"}`}>
                                            {item.pricingStatus ?? "—"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {(!cart.items || cart.items.length === 0) && (
                                <tr>
                                    <td colSpan={7} className="pl-3 py-6 text-center text-gray-400">
                                        No items in this cart.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Grounding & Pricing Detail — hidden for now
            {pricingLines.length > 0 && (
                <div className="bg-white rounded-2xl shadow-soft-xl p-5">
                    <h2 className="text-lg font-semibold font-sans text-gray-700 mb-1 flex items-center gap-2">
                        <MdInsights className="text-[#069079]" style={{ fontSize: "22px" }} />
                        Grounding & Pricing Detail
                    </h2>
                    <p className="text-xs text-gray-400 font-sans mb-4">
                        Per-medicine grounding decision and pricing source breakdown. Click a row to expand source attempts.
                    </p>
                    <div className="grid grid-cols-[2fr_2fr_2fr_1fr] gap-4 px-5 mb-2">
                        {["Medicine", "Grounding", "Pricing", "Sources"].map((h) => (
                            <p key={h} className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 font-sans">
                                {h}
                            </p>
                        ))}
                    </div>
                    {pricingLines.map((line, i) => (
                        <MedicineRow key={line.cartItemId ?? i} line={line} />
                    ))}
                </div>
            )}
            */}

            {/* Totals Footer */}
            <div className="flex items-center justify-between gap-3 p-5 bg-[#069079] rounded-xl shadow">
                <div className="flex gap-8">
                    <div>
                        <div className="text-sm text-gray-100 font-medium">Items Total</div>
                        <div className="text-xl font-semibold text-white-50">
                            {cart.itemsTotal?.toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }) || "₹0.00"}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-100 font-medium">MRP Total</div>
                        <div className="text-xl font-semibold text-white-50">
                            {cart.mrpTotal?.toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }) || "₹0.00"}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-100 font-medium">Savings</div>
                        <div className="text-xl font-semibold text-white-50">
                            {cart.savings?.toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }) || "₹0.00"}
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}
